import { api } from './api'

export interface KPICard {
  title: string
  value: number
  change_percent?: number
  change_label?: string
  icon?: string
  color?: string
}

export interface KPISummaryResponse {
  total_appointments: number
  total_revenue: number
  total_patients: number
  avg_wait_time_minutes: number
  kpi_cards: KPICard[]
}

export interface AppointmentByStatus {
  status: string
  count: number
  percentage: number
}

export interface AppointmentByDepartment {
  department: string
  count: number
  percentage: number
}

export interface AppointmentByType {
  appointment_type: string
  count: number
}

export interface DailyAppointmentMetrics {
  date: string
  scheduled: number
  completed: number
  cancelled: number
  no_show: number
}

export interface AppointmentAnalyticsResponse {
  total_appointments: number
  by_status: AppointmentByStatus[]
  by_department: AppointmentByDepartment[]
  by_type: AppointmentByType[]
  daily_trends: DailyAppointmentMetrics[]
  avg_duration_minutes: number
  no_show_rate: number
  cancellation_rate: number
}

export interface RevenueByDepartment {
  department: string
  total_revenue: number
  invoice_count: number
  percentage: number
}

export interface RevenueByPaymentMethod {
  payment_method: string
  total_amount: number
  transaction_count: number
}

export interface DailyRevenueMetrics {
  date: string
  total_revenue: number
  invoice_count: number
  payment_count: number
  outstanding_amount: number
}

export interface RevenueAnalyticsResponse {
  total_revenue: number
  total_outstanding: number
  total_collected: number
  collection_rate: number
  average_invoice_amount: number
  by_department: RevenueByDepartment[]
  by_payment_method: RevenueByPaymentMethod[]
  daily_trends: DailyRevenueMetrics[]
}

export interface PatientByDepartment {
  department: string
  count: number
  percentage: number
}

export interface PatientGrowthMetrics {
  date: string
  new_patients: number
  total_patients: number
  active_patients: number
}

export interface PatientDemographics {
  age_group: string
  count: number
  percentage: number
}

export interface PatientAnalyticsResponse {
  total_patients: number
  new_patients_this_month: number
  active_patients: number
  by_department: PatientByDepartment[]
  demographics: PatientDemographics[]
  daily_growth: PatientGrowthMetrics[]
}

export interface DepartmentPerformanceMetrics {
  department: string
  appointment_count: number
  revenue: number
  patient_count: number
  utilization_rate: number
  avg_appointment_duration: number
}

export interface DepartmentComparisonResponse {
  departments: DepartmentPerformanceMetrics[]
  top_performing: string
  needs_attention: string[]
}

export interface AnalyticsOverviewResponse {
  kpi_summary: KPISummaryResponse
  appointments: AppointmentAnalyticsResponse
  revenue: RevenueAnalyticsResponse
  patients: PatientAnalyticsResponse
  departments: DepartmentComparisonResponse
  generated_at: string
}

export interface DateRangeParams {
  start_date?: string
  end_date?: string
  department?: string
}

export const analyticsApi = {
  getOverview: (params?: DateRangeParams) =>
    api.get<AnalyticsOverviewResponse>('/analytics/overview', { params }),

  getKPISummary: (params?: DateRangeParams) =>
    api.get<KPISummaryResponse>('/analytics/kpi-summary', { params }),

  getAppointmentAnalytics: (params?: DateRangeParams) =>
    api.get<AppointmentAnalyticsResponse>('/analytics/appointments', { params }),

  getRevenueAnalytics: (params?: DateRangeParams) =>
    api.get<RevenueAnalyticsResponse>('/analytics/revenue', { params }),

  getPatientAnalytics: (params?: DateRangeParams) =>
    api.get<PatientAnalyticsResponse>('/analytics/patients', { params }),

  getDepartmentComparison: (params?: DateRangeParams) =>
    api.get<DepartmentComparisonResponse>('/analytics/departments', { params }),
}
